import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ShieldCheck, UserPlus, EnvelopeSimple, Clock, ArrowClockwise, Trash, Copy, Check, Crown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../components/ConfirmDialog';
import { toast } from 'sonner';
import AppFooter from '../components/AppFooter';

const ROLE_COLORS = {
  owner: 'bg-amber-100 text-amber-700 border border-amber-200',
  admin: 'bg-red-100 text-red-700 border border-red-200',
  project_manager: 'bg-blue-100 text-blue-700 border border-blue-200',
  contributor: 'bg-green-100 text-green-700 border border-green-200',
  viewer: 'bg-zinc-100 text-zinc-600 border border-zinc-200',
};
const ROLE_LABELS = {
  owner: 'Owner', admin: 'Admin', project_manager: 'Project Manager', contributor: 'Contributor', viewer: 'Viewer',
};

export default function TeamPage() {
  const { user, currentOrg, getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('contributor');
  const [inviting, setInviting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [busyInvId, setBusyInvId] = useState(null);
  const [limitError, setLimitError] = useState(null);

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const fetchMembers = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/members`, h()); setMembers(data); }
    catch (err) { console.error(err); }
  }, [currentOrg, API, h]);

  const fetchInvitations = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/invitations`, h()); setInvitations(data); }
    catch (err) { /* viewer/contributor cannot list; silently ignore */ }
  }, [currentOrg, API, h]);

  useEffect(() => { fetchMembers(); fetchInvitations(); }, [fetchMembers, fetchInvitations]);

  const handleRoleChange = async (userId, newRole) => {
    try { await axios.put(`${API}/members/${userId}/role`, { role: newRole }, h()); fetchMembers(); }
    catch (err) { console.error(err); }
  };

  const handleRemoveMember = async (m) => {
    if (!(await confirm({ title: `Remove ${m.name || m.email}?`, description: 'They will immediately lose access to this organization.', confirmText: 'Remove', variant: 'destructive' }))) return;
    try { await axios.delete(`${API}/members/${m.user_id}`, h()); fetchMembers(); toast.success('Member removed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove member'); }
  };

  const handleTransferOwnership = async (m) => {
    if (!(await confirm({
      title: `Make ${m.name || m.email} the owner?`,
      description: `${m.name || m.email} will become the workspace Owner with full control. You will be changed to Admin. This cannot be undone by you afterwards — only the new owner can transfer it back.`,
      confirmText: 'Transfer ownership',
      variant: 'destructive',
    }))) return;
    try {
      await axios.put(`${API}/members/${m.user_id}/transfer-ownership`, {}, h());
      toast.success('Ownership transferred');
      await fetchMembers();
      // Role for the current user changed (owner → admin); refresh org context.
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to transfer ownership'); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setLimitError(null);
    try {
      await axios.post(`${API}/members/invite`, { email: inviteEmail.trim(), role: inviteRole }, h());
      setInviteEmail(''); setShowInvite(false); fetchMembers(); fetchInvitations();
    } catch (err) {
      const code = err.response?.data?.error_code;
      if (code === 'free_limit' || code === 'seat_limit' || code === 'trial_expired' || code === 'inactive') {
        setShowInvite(false);
        setLimitError({ message: err.response?.data?.detail, code });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to send invitation');
      }
    } finally { setInviting(false); }
  };

  const handleResend = async (inv) => {
    setBusyInvId(inv.invitation_id);
    try { await axios.post(`${API}/invitations/${inv.invitation_id}/resend`, {}, h()); fetchInvitations(); toast.success('Invitation resent'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend invitation'); }
    finally { setBusyInvId(null); }
  };

  const handleRevoke = async (inv) => {
    if (!(await confirm({ title: `Revoke invitation to ${inv.email}?`, description: 'This invitation link will stop working.', confirmText: 'Revoke', variant: 'destructive' }))) return;
    setBusyInvId(inv.invitation_id);
    try { await axios.delete(`${API}/invitations/${inv.invitation_id}`, h()); fetchInvitations(); toast.success('Invitation revoked'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to revoke invitation'); }
    finally { setBusyInvId(null); }
  };

  const handleCopyLink = async (inv) => {
    // Invitation links open the FRONTEND login page, so always use the
    // current browser origin — NOT REACT_APP_BACKEND_URL which points at
    // the API (e.g. `https://projectsuat.inspiroworks.com/backend` on
    // production VPS deployments).
    const base = window.location.origin.replace(/\/$/, '');
    const link = `${base}/login?invitation=${encodeURIComponent(inv.token)}&email=${encodeURIComponent(inv.email)}`;
    try { await navigator.clipboard.writeText(link); setCopiedId(inv.invitation_id); setTimeout(() => setCopiedId(null), 2000); }
    catch { window.prompt('Copy this invitation link:', link); }
  };

  const canManage = ['owner', 'admin'].includes(currentOrg?.role);
  const isOwner = currentOrg?.role === 'owner';
  const canInvite = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);
  const canRevoke = ['owner', 'admin'].includes(currentOrg?.role);

  return (
    <div className="flex-1 overflow-auto" data-testid="team-page">
      <div className="px-8 py-6 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium mb-1">{currentOrg?.org_name}</p>
          <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900 tracking-tight">Team Members</h1>
        </div>
        {canInvite && (
          <Button data-testid="invite-member-button" onClick={() => setShowInvite(true)} className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm">
            <UserPlus size={16} className="mr-1" /> Invite Member
          </Button>
        )}
      </div>
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-8">
          {Object.entries(ROLE_LABELS).map(([role, label]) => {
            const count = members.filter(m => m.role === role).length;
            return (
              <div key={role} className="bg-white border border-zinc-200 rounded-lg p-4">
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">{label}s</span>
                <p className="font-['Outfit'] text-2xl font-black text-zinc-900 tracking-tight mt-1">{count}</p>
              </div>
            );
          })}
        </div>
        <div className="border border-zinc-200 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-zinc-200 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium">Member</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-48">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-28">Joined</TableHead>
                {canManage && <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-20">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.user_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200" data-testid={`team-member-${m.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={m.avatar} />
                        <AvatarFallback className={`${getAvatarStyle(m.user_id || m.name).bg} text-white text-xs font-bold`}>{getInitials(m.name)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-zinc-900">{m.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">{m.email}</TableCell>
                  <TableCell>
                    {canManage && m.role !== 'owner' && m.user_id !== user?.user_id ? (
                      <Select value={m.role} onValueChange={(v) => handleRoleChange(m.user_id, v)}>
                        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`role-select-${m.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="project_manager">Project Manager</SelectItem>
                          <SelectItem value="contributor">Contributor</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`text-[10px] ${ROLE_COLORS[m.role] || ROLE_COLORS.viewer}`}>
                        {m.user_id === user?.user_id && <ShieldCheck size={10} className="mr-1" />}
                        {ROLE_LABELS[m.role] || m.role}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-400">{m.created_at ? new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {isOwner && m.role !== 'owner' && m.user_id !== user?.user_id && (
                          <button
                            onClick={() => handleTransferOwnership(m)}
                            className="w-7 h-7 flex items-center justify-center rounded text-zinc-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                            title="Make owner"
                            data-testid={`make-owner-${m.user_id}`}
                          >
                            <Crown size={14} />
                          </button>
                        )}
                        {m.role !== 'owner' && m.user_id !== user?.user_id && (
                          <button
                            onClick={() => handleRemoveMember(m)}
                            className="w-7 h-7 flex items-center justify-center rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Remove member"
                            data-testid={`remove-member-${m.user_id}`}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pending Invitations */}
        {canInvite && invitations.length > 0 && (
          <div className="mt-10" data-testid="pending-invitations-section">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} className="text-amber-500" />
              <h2 className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">Pending Invitations</h2>
              <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200">{invitations.length}</Badge>
            </div>
            <div className="border border-zinc-200 rounded-lg bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-40">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-40">Invited By</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-32">Sent</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-medium w-56 text-right pr-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((inv) => (
                    <TableRow key={inv.invitation_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`invitation-${inv.invitation_id}`}>
                      <TableCell className="text-sm text-zinc-800 font-medium">{inv.email}</TableCell>
                      <TableCell><Badge className={`text-[10px] ${ROLE_COLORS[inv.role] || ROLE_COLORS.viewer}`}>{ROLE_LABELS[inv.role] || inv.role}</Badge></TableCell>
                      <TableCell className="text-xs text-zinc-500">{inv.inviter_name || inv.inviter_email || '-'}</TableCell>
                      <TableCell className="text-xs text-zinc-400">{inv.last_resent_at ? `Resent ${new Date(inv.last_resent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1 pr-2">
                          <button
                            onClick={() => handleCopyLink(inv)}
                            className="h-7 px-2 flex items-center gap-1 text-[11px] text-zinc-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Copy invite link"
                            data-testid={`copy-invitation-${inv.invitation_id}`}
                          >
                            {copiedId === inv.invitation_id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                          </button>
                          <button
                            onClick={() => handleResend(inv)}
                            disabled={busyInvId === inv.invitation_id}
                            className="h-7 px-2 flex items-center gap-1 text-[11px] text-zinc-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="Resend invitation email"
                            data-testid={`resend-invitation-${inv.invitation_id}`}
                          >
                            <ArrowClockwise size={12} /> Resend
                          </button>
                          {canRevoke && (
                            <button
                              onClick={() => handleRevoke(inv)}
                              disabled={busyInvId === inv.invitation_id}
                              className="h-7 px-2 flex items-center gap-1 text-[11px] text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Revoke invitation"
                              data-testid={`revoke-invitation-${inv.invitation_id}`}
                            >
                              <Trash size={12} /> Revoke
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="sm:max-w-md" data-testid="invite-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg font-bold text-zinc-900 tracking-tight">Invite Team Member</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">Add a member to {currentOrg?.org_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">Email Address</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                <Input data-testid="invite-email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@company.com" className="pl-9" type="email" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-medium">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="contributor">Contributor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="submit-invite" disabled={inviting} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white">
              {inviting ? 'Inviting...' : 'Send Invitation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!limitError} onOpenChange={() => setLimitError(null)}>
        <DialogContent data-testid="upgrade-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="text-amber-500" weight="fill" size={22} />
              Upgrade to invite more users
            </DialogTitle>
            <DialogDescription className="pt-2 text-zinc-600">{limitError?.message}</DialogDescription>
          </DialogHeader>
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg p-4 text-sm">
            <p className="font-semibold text-indigo-900 mb-1">Business Plan</p>
            <p className="text-indigo-700 text-[13px]">Unlimited users · Advanced custom fields · 14-day free trial · Starts at ₹199 / user / month</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setLimitError(null)}>Not now</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setLimitError(null); navigate('/billing'); }} data-testid="upgrade-cta-button">
              <Crown size={14} className="mr-1.5" /> Upgrade to Business
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AppFooter />
    </div>
  );
}
